import { has } from 'lodash';
import {
  sendApprovalEmailToDriver,
  sendTypeEmailToDriver,
  sendRejectionEmailToDriver,
  sendAwaitingDocsEmailToDriver,
  sendBackgroundCheckEmailToDriver,
  sendWaitlistEmailToDriver,
} from '../Email/actions';
import moment from 'moment';

import api from '../../lib/api';
import { apiErrorMessage } from '../../helpers/api';

export const DriverActionsTypes = {
  FETCH_DRIVERS_STARTED: 'FETCH_DRIVERS_STARTED',
  FETCH_DRIVERS_SUCCESS: 'FETCH_DRIVERS_SUCCESS',
  FETCH_DRIVERS_FAILURE: 'FETCH_DRIVERS_FAILURE',
  FETCH_DRIVERS_COUNT_STARTED: 'FETCH_DRIVERS_COUNT_STARTED',
  FETCH_DRIVERS_COUNT_SUCCESS: 'FETCH_DRIVERS_COUNT_SUCCESS',
  FETCH_DRIVERS_COUNT_FAILURE: 'FETCH_DRIVERS_COUNT_FAILURE',
  FETCH_DRIVER_STARTED: 'FETCH_DRIVER_STARTED',
  FETCH_DRIVER_SUCCESS: 'FETCH_DRIVER_SUCCESS',
  FETCH_DRIVER_FAILURE: 'FETCH_DRIVER_FAILURE',
  EDIT_DRIVER_BYID_STARTED: 'EDIT_DRIVER_BYID_STARTED',
  EDIT_DRIVER_BYID_SUCCESS: 'EDIT_DRIVER_BYID_SUCCESS',
  EDIT_DRIVER_BYID_FAILURE: 'EDIT_DRIVER_BYID_FAILURE',
  UPLOAD_IMAGE_STARTED: 'UPLOAD_IMAGE_STARTED',
  UPLOAD_IMAGE_SUCCESS: 'UPLOAD_IMAGE_SUCCESS',
  UPLOAD_IMAGE_FAILURE: 'UPLOAD_IMAGE_FAILURE',
  BACKGROUND_CHECK_STARTED: 'BACKGROUND_CHECK_STARTED',
  BACKGROUND_CHECK_SUCCESS: 'BACKGROUND_CHECK_SUCCESS',
  BACKGROUND_CHECK_FAILURE: 'BACKGROUND_CHECK_FAILURE',
  DRIVER_LOCATION_UPDATE: 'DRIVER_LOCATION_UPDATE',
  RESET_STATE: 'RESET_STATE',
};

export const fetchDrivers =
  ({ offset = 0, limit = 11, driverApplicationStatus, homeCity } = {}) =>
  async (dispatch) => {
    dispatch({
      type: DriverActionsTypes.FETCH_DRIVERS_STARTED,
      payload: { offset, limit, driverApplicationStatus, homeCity },
    });
    try {
      const drivers = await api.drivers.getDrivers({ offset, limit, driverApplicationStatus, homeCity });
      dispatch({
        type: DriverActionsTypes.FETCH_DRIVERS_SUCCESS,
        payload: drivers,
      });
    } catch (error) {
      dispatch({
        type: DriverActionsTypes.FETCH_DRIVERS_FAILURE,
        payload: apiErrorMessage(error),
      });
    }
  };

export const fetchDriverById = (id) => async (dispatch) => {
  dispatch({
    type: DriverActionsTypes.FETCH_DRIVER_STARTED,
  });
  try {
    const driver = await api.drivers.getDriverWithSignedImageUrls(id);
    dispatch({
      type: DriverActionsTypes.FETCH_DRIVER_SUCCESS,
      payload: driver,
    });
  } catch (error) {
    dispatch({
      type: DriverActionsTypes.FETCH_DRIVER_FAILURE,
      payload: apiErrorMessage(error),
    });
  }
};

export const editDriver = (driver, update) => async (dispatch) => {
  try {
    dispatch({
      type: DriverActionsTypes.EDIT_DRIVER_BYID_STARTED,
    });

    if (has(update, 'dob')) {
      update.dob = update.dob ? moment.utc(update.dob).format('YYYY-MM-DD') : null;
    }
    if (has(update, 'licenseExpDate')) {
      update.licenseExpDate = update.licenseExpDate ? moment.utc(update.licenseExpDate).toISOString() : null;
    }
    if (has(update, 'insuranceExpDate')) {
      update.insuranceExpDate = update.insuranceExpDate ? moment.utc(update.insuranceExpDate).toISOString() : null;
    }
    if (has(update, 'vehicles')) {
      update.vehicles = update.vehicles.map((v) => ({
        active: v.active,
        licensePlate: v.licensePlate,
        make: v.make,
        model: v.model,
        type: v.type,
        vin: v.vin === '' ? null : v.vin,
        year: v.year === '' ? null : v.year,
        seatCount: v.seatCount === '' ? null : Number.parseInt(v.seatCount),
        registrationExpDate: v.registrationExpDate ? moment.utc(v.registrationExpDate, 'MM/YYYY').toISOString() : null,
        registrationImageUrl: v.registrationImageUrl || null,
        registrationInvalid: v.registrationInvalid,
      }));
    }

    const updatedDriver = await api.drivers.updateDriver(driver.driver_id, update);
    dispatch({
      type: DriverActionsTypes.EDIT_DRIVER_BYID_SUCCESS,
      payload: updatedDriver,
    });
    // send email to driver
    const canSendEmail = updatedDriver.email && !!updatedDriver.driverApplicationStatus;
    const shouldSendEmail = has(update, 'driverApplicationStatus') || has(update, 'isCustomDriver');
    if (canSendEmail && shouldSendEmail) {
      switch (updatedDriver.driverApplicationStatus) {
        case 'approved':
          sendApprovalEmailToDriver(updatedDriver.email)(dispatch);
          sendTypeEmailToDriver(updatedDriver.firstName, updatedDriver.email, updatedDriver.isCustomDriver)(dispatch);
          break;
        case 'rejected':
          sendRejectionEmailToDriver(updatedDriver.firstName, updatedDriver.email)(dispatch);
          break;
        case 'awaiting_docs':
          sendAwaitingDocsEmailToDriver(
            {
              license: updatedDriver.invalidDocuments.includes('license'),
              registration: updatedDriver.invalidDocuments.includes('registration'),
              insurance: updatedDriver.invalidDocuments.includes('insurance'),
            },
            updatedDriver.email,
          )(dispatch);
          break;
        case 'background_check':
          sendBackgroundCheckEmailToDriver(updatedDriver.email)(dispatch);
          break;
        case 'waitlist':
          sendWaitlistEmailToDriver(updatedDriver.email)(dispatch);
          break;
        default:
          break;
      }
    }
    // run background check on driver if needed
    has(update, 'driverApplicationStatus') && backgroundCheck(updatedDriver)(dispatch);

    return { success: true };
  } catch (error) {
    dispatch({
      type: DriverActionsTypes.EDIT_DRIVER_BYID_FAILURE,
      payload: apiErrorMessage(error),
    });
    return { success: false };
  }
};

export const decodeVIN = (vin) => async () => {
  try {
    const [vinResponse, mappingsResponse] = await Promise.allSettled([
      api.drivers.decodeVIN(vin),
      fetch(
        'https://docs.google.com/spreadsheets/d/12rCRwQGWD2_26EcGF_pulHU5nXu6_hsu3-R7-y4bWP8/gviz/tq?tqx=out:csv&sheet=Types',
      ),
    ]);
    if (vinResponse.status === 'rejected') {
      throw vinResponse.reason;
    }

    const vinData = vinResponse.value;

    // Parse CSV mappings
    let mappings = {};
    try {
      if (mappingsResponse.status === 'rejected') {
        throw mappingsResponse.reason;
      }
      const mappingsText = await mappingsResponse.value.text();
      const rows = mappingsText.split('\n').slice(1);
      rows.forEach((row) => {
        const [key, value] = row.split(',').map((cell) => cell.trim().replace(/"/g, ''));
        if (key && value) {
          mappings[key] = value;
        }
      });
    } catch (err) {
      console.error('Error fetching vehicle mappings:', err);
      mappings = {};
    }

    // Try to match make+model with and without year
    const withYear = `${vinData.year} ${vinData.make} ${vinData.model}`;
    const withoutYear = `${vinData.make} ${vinData.model}`;

    const data = {
      ...vinData,
      type: mappings[withYear] || mappings[withoutYear] || vinData.type,
    };

    return { success: true, data };
  } catch (err) {
    return { success: false };
  }
};

export const getDocumentUploadUrl = (id) => async () => {
  try {
    const data = await api.drivers.getDocumentUploadUrl(id);
    return { success: true, data };
  } catch (err) {
    return { success: false };
  }
};

// Read a driver's license image (signed URL) for the profile-photo bypass
// compare view. Keyed by driver_id (the same id getDriverWithSignedImageUrls
// takes elsewhere), NOT the user id. Distinguishes a fetch failure
// (success: false — the caller should show a retry/error state) from a driver
// who genuinely has no license on file (success: true, licenseImageUrl: null —
// the caller disables the bypass). Collapsing the two would let a transient
// error masquerade as "no license" and block a legitimate override.
export const getDriverLicenseImage = (driverId) => async () => {
  try {
    const driver = await api.drivers.getDriverWithSignedImageUrls(driverId);
    return { success: true, licenseImageUrl: driver?.licenseImageUrl || null };
  } catch (error) {
    return { success: false };
  }
};

export const backgroundCheck = (user) => async (dispatch) => {
  try {
    if (user.driverApplicationStatus === 'background_check_invitation' && user.candidate_id) {
      // checkr
      dispatch({
        type: DriverActionsTypes.BACKGROUND_CHECK_STARTED,
      });
      await api.drivers.createScreeningInvitation(user.driver_id);
      dispatch({
        type: DriverActionsTypes.BACKGROUND_CHECK_SUCCESS,
        payload: {},
      });
      return { success: true };
    }

    if (user.driverApplicationStatus === 'background_check' && user.candidate_id) {
      dispatch({
        type: DriverActionsTypes.BACKGROUND_CHECK_STARTED,
      });
      await api.drivers.createScreeningReport(user.driver_id);
      dispatch({
        type: DriverActionsTypes.BACKGROUND_CHECK_SUCCESS,
        payload: {},
      });
      return { success: true };
    }

    return { success: true };
  } catch (e) {
    return { success: false };
  }
};
